import {
  addDoc,
  collection,
  deleteDoc,
  doc,
  onSnapshot,
  orderBy,
  query,
  runTransaction,
  serverTimestamp,
  updateDoc,
} from 'firebase/firestore'
import type { DocumentData, QueryDocumentSnapshot } from 'firebase/firestore'
import { db, firebaseErrorCode, logFirebaseError, requireSession } from '../lib/firebase'
import { timestampToIso } from './customersService'
import { stripUndefined } from '../utils/firestore'
import type { Quote } from '../types/quote'

export const quotesRef = collection(db, 'quotes')

/** Tæller der sikrer unikke tilbudsnumre — numre genbruges aldrig. */
const counterDoc = doc(db, 'counters', 'quotes')

function toQuote(snapshot: QueryDocumentSnapshot<DocumentData>): Quote {
  const data = snapshot.data()
  return {
    id: snapshot.id,
    quoteNumber: data.quoteNumber ?? '',
    customerId: data.customerId ?? '',
    customerName: data.customerName ?? '',
    customerEmail: data.customerEmail ?? '',
    customerPhone: data.customerPhone ?? '',
    customerAddress: data.customerAddress ?? '',
    enquiryId: data.enquiryId ?? undefined,
    caseId: data.caseId ?? undefined,
    title: data.title ?? '',
    description: data.description ?? '',
    lines: Array.isArray(data.lines) ? data.lines : [],
    subtotal: Number(data.subtotal) || 0,
    vatAmount: Number(data.vatAmount) || 0,
    total: Number(data.total) || 0,
    status: data.status ?? 'Kladde',
    quoteDate: data.quoteDate ?? '',
    validUntil: data.validUntil ?? '',
    internalNotes: data.internalNotes ?? '',
    customerNotes: data.customerNotes ?? '',
    intro: data.intro ?? '',
    terms: data.terms ?? '',
    closing: data.closing ?? '',
    paymentTerms: data.paymentTerms ?? '',
    createdAt: timestampToIso(data.createdAt),
    updatedAt: timestampToIso(data.updatedAt),
    sentAt: timestampToIso(data.sentAt) || undefined,
    acceptedAt: timestampToIso(data.acceptedAt) || undefined,
    rejectedAt: timestampToIso(data.rejectedAt) || undefined,
    emailStatus: data.emailStatus ?? 'ikke sendt',
    emailSentAt: timestampToIso(data.emailSentAt) || undefined,
    dineroInvoiceId: data.dineroInvoiceId ?? undefined,
  }
}

export function subscribeQuotes(
  onChange: (quotes: Quote[]) => void,
  onError: (error: unknown) => void,
): () => void {
  return onSnapshot(
    query(quotesRef, orderBy('createdAt', 'desc')),
    (snapshot) => onChange(snapshot.docs.map(toQuote)),
    onError,
  )
}

/**
 * Reserverer det næste tilbudsnummer i en transaktion, fx TIL-2026-0001.
 * Tælleren nulstilles ved årsskifte og genbruger aldrig et brugt nummer.
 */
export async function reserveQuoteNumber(existingNumbers: string[] = []): Promise<string> {
  requireSession('reserveQuoteNumber')
  const year = new Date().getFullYear()

  try {
    const next = await runTransaction(db, async (transaction) => {
      const snapshot = await transaction.get(counterDoc)
      const data = snapshot.exists() ? snapshot.data() : undefined
      const sameYear = data?.year === year
      const value = sameYear ? Number(data?.lastNumber ?? 0) + 1 : 1
      transaction.set(counterDoc, { year, lastNumber: value, updatedAt: serverTimestamp() })
      return value
    })
    return `TIL-${year}-${String(next).padStart(4, '0')}`
  } catch (error) {
    // Tælleren ligger i "counters". Er den ikke tilladt i Firestore Rules,
    // må tilbuddet stadig kunne gemmes — nummeret udledes så af de
    // eksisterende tilbud i stedet for at blokere hele oprettelsen.
    if (firebaseErrorCode(error) !== 'permission-denied') throw error
    logFirebaseError('reserveQuoteNumber (counters) — bruger reservenummerering', error)

    const highest = existingNumbers.reduce((max, number) => {
      const match = /^TIL-(\d{4})-(\d+)$/.exec(number)
      if (!match || Number(match[1]) !== year) return max
      return Math.max(max, Number(match[2]))
    }, 0)
    return `TIL-${year}-${String(highest + 1).padStart(4, '0')}`
  }
}

export async function createQuoteDoc(
  data: Omit<Quote, 'id' | 'createdAt' | 'updatedAt'>,
): Promise<string> {
  requireSession('createQuoteDoc')
  const created = await addDoc(quotesRef, {
    ...stripUndefined(data as unknown as Record<string, unknown>),
    createdAt: serverTimestamp(),
    updatedAt: serverTimestamp(),
  })
  return created.id
}

export async function updateQuoteDoc(id: string, patch: Partial<Quote>): Promise<void> {
  requireSession('updateQuoteDoc')
  await updateDoc(doc(db, 'quotes', id), {
    ...stripUndefined(patch as Record<string, unknown>),
    updatedAt: serverTimestamp(),
  })
}

export async function deleteQuoteDoc(id: string): Promise<void> {
  requireSession('deleteQuoteDoc')
  await deleteDoc(doc(db, 'quotes', id))
}
